import { useIntl, IntlShape } from 'react-intl';
import { Label } from 'ui/Label';
import {
  ApplicationDeliveryCondition,
  DeliveryConditionsData,
  DeliveryProductReference,
  Listing,
  ListingProduct,
} from '../../../../../types/ListingData';
import { formatDateRange } from '../../../../../utils/dateHelpers';

interface DeliveriesProps {
  displayDeliveries: (ApplicationDeliveryCondition | DeliveryConditionsData)[];
  listingData?: Listing | null;
  warehouseAddress?: string;
  farmProductNames?: { [key: number]: string };
  applicationProducts?: Array<{ listing_product_id: string; farm_product_id?: number }>;
}

const formatFrequency = (
  value: number | undefined,
  unit: string | undefined,
  intl: IntlShape
): string => {
  if (!value || !unit) return '';
  const translatedUnit = intl.formatMessage({
    id: `enum.${unit.toUpperCase()}`,
  });
  return `(${value} ${translatedUnit})`;
};

const getDeliveryDateRange = (
  delivery: ApplicationDeliveryCondition | DeliveryConditionsData,
  listingDelivery: DeliveryConditionsData | null
): string => {
  if (!listingDelivery) {
    return formatDateRange(
      delivery.delivery_start_date,
      delivery.delivery_end_date
    );
  }

  if (listingDelivery.delivery_type === 'delivery_conditions') {
    return formatDateRange(
      listingDelivery.contract_start_date,
      listingDelivery.contract_end_date
    );
  }

  if (listingDelivery.delivery_type === 'detailed_delivery') {
    return formatDateRange(
      listingDelivery.delivery_start_date,
      listingDelivery.delivery_end_date
    );
  }

  return formatDateRange(
    delivery.delivery_start_date,
    delivery.delivery_end_date
  );
};

const getDeliveryInfo = (
  delivery: ApplicationDeliveryCondition | DeliveryConditionsData,
  listingData: Listing | null | undefined,
  intl: IntlShape
) => {
  const isAcceptingAll =
    ('is_accepting_all' in delivery && delivery.is_accepting_all) ||
    ('is_all_products' in delivery && delivery.is_all_products);
  const listingDeliveryConditionId =
    'listing_delivery_condition_id' in delivery
      ? delivery.listing_delivery_condition_id
      : undefined;

  const shouldUseListingData = isAcceptingAll && listingDeliveryConditionId;
  const listingDelivery = shouldUseListingData
    ? listingData?.delivery_conditions?.find(
        (condition) => condition.id === listingDeliveryConditionId
      )
    : null;

  const dateRange = listingDelivery
    ? getDeliveryDateRange(delivery, listingDelivery)
    : formatDateRange(delivery.delivery_start_date, delivery.delivery_end_date);

  const frequency = listingDelivery
    ? formatFrequency(
        listingDelivery.delivery_frequency_value,
        listingDelivery.delivery_frequency_unit,
        intl
      )
    : formatFrequency(
        delivery.delivery_frequency_value,
        delivery.delivery_frequency_unit,
        intl
      );

  return { dateRange, frequency, isAcceptingAll };
};

const getProductDisplayName = (
  listingProduct: ListingProduct | undefined,
  listingProductId: string | undefined,
  farmProductNames: { [key: number]: string } | undefined,
  applicationProducts: Array<{ listing_product_id: string; farm_product_id?: number }> | undefined
): string => {
  if (!listingProduct) return '';
  const appProduct = applicationProducts?.find(
    (ap) => String(ap.listing_product_id) === String(listingProductId)
  );
  const selectedName =
    appProduct?.farm_product_id && farmProductNames?.[appProduct.farm_product_id]
      ? farmProductNames[appProduct.farm_product_id]
      : null;
  return selectedName || listingProduct.name || listingProduct.category?.name || '';
};

export const Deliveries = ({
  displayDeliveries,
  listingData,
  warehouseAddress,
  farmProductNames = {},
  applicationProducts = [],
}: DeliveriesProps) => {
  const intl = useIntl();

  if (displayDeliveries.length === 0) {
    return null;
  }

  return (
    <div className="summary-section">
      <Label
        label={intl.formatMessage({
          id: 'listings.application.deliveries',
        })}
        title={true}
      />
      <div className="deliveries-list">
        {displayDeliveries.map((delivery, index) => {
          const { dateRange, frequency, isAcceptingAll } = getDeliveryInfo(
            delivery,
            listingData,
            intl
          );

          const headerParts = [
            dateRange !== '-' ? dateRange : null,
            frequency?.trim() || null,
          ].filter(Boolean) as string[];

          return (
            <div key={delivery.id || index} className="delivery-card">
              <div className="delivery-header">
                <Label label={index + 1} className="delivery-number" />
                {headerParts.length > 0 && (
                  <div className="delivery-info">
                    <Label title={true} label={headerParts.join(' ')} />
                  </div>
                )}
              </div>

              <div className="delivery-content">
                {isAcceptingAll ? (
                  (() => {
                    const listingDeliveryConditionId =
                      'listing_delivery_condition_id' in delivery
                        ? delivery.listing_delivery_condition_id
                        : undefined;

                    let productsToDisplay: Array<{
                      id?: string;
                      categoryName: string;
                      quantity: number | string;
                      storageUnit: string;
                    }> = [];

                    if (
                      listingDeliveryConditionId &&
                      listingData?.delivery_conditions
                    ) {
                      const listingDelivery =
                        listingData.delivery_conditions.find(
                          (condition) =>
                            condition.id === listingDeliveryConditionId
                        );

                      if (listingDelivery && listingDelivery.products) {
                        const conditionProducts = Array.isArray(
                          listingDelivery.products
                        )
                          ? listingDelivery.products
                          : [];

                        const mappedProducts = conditionProducts.map(
                          (
                            p: DeliveryProductReference | ListingProduct
                          ): {
                            id: string;
                            categoryName: string;
                            quantity: number | string;
                            storageUnit: string;
                          } | null => {
                            let productId: string | undefined;
                            let quantity: number | string | undefined;

                            if ('listing_product_id' in p) {
                              const productRef = p as DeliveryProductReference;
                              productId =
                                productRef.listing_product_id ??
                                (productRef.id ? String(productRef.id) : undefined) ??
                                productRef.listing_product?.id;
                              quantity =
                                productRef.delivery_quantity ??
                                productRef.product_quantity;
                            } else {
                              const listingProd = p as ListingProduct;
                              productId = listingProd.id;
                              quantity = listingProd.required_quantity;
                            }

                            if (!productId) return null;

                            const listingProduct = listingData?.products?.find(
                              (lp) => lp.id === productId
                            );

                            if (!listingProduct || !listingProduct.id)
                              return null;

                            const finalQuantity =
                              quantity ?? listingProduct.required_quantity ?? 0;

                            return {
                              id: listingProduct.id,
                              categoryName: getProductDisplayName(
                                listingProduct,
                                productId,
                                farmProductNames,
                                applicationProducts
                              ),
                              quantity: finalQuantity,
                              storageUnit:
                                listingProduct.storage_unit?.short ||
                                listingProduct.storage_unit?.name ||
                                '',
                            };
                          }
                        );

                        productsToDisplay = mappedProducts.filter(
                          (p): p is NonNullable<(typeof mappedProducts)[0]> => {
                            return p !== null && p.categoryName.trim() !== '';
                          }
                        );
                      }
                    } else if (
                      'products' in delivery &&
                      Array.isArray(delivery.products)
                    ) {
                      const deliveryProducts = delivery.products as Array<{
                        listing_product_id?: string;
                        product_quantity?: number;
                      }>;

                      const mappedDeliveryProducts = deliveryProducts.map(
                        (p) => {
                          const listingProduct = listingData?.products?.find(
                            (lp) => lp.id === p.listing_product_id
                          );

                          if (!listingProduct || !p.product_quantity)
                            return null;

                          return {
                            id: listingProduct.id,
                            categoryName: getProductDisplayName(
                              listingProduct,
                              p.listing_product_id,
                              farmProductNames,
                              applicationProducts
                            ),
                            quantity: p.product_quantity,
                            storageUnit:
                              listingProduct.storage_unit?.short ||
                              listingProduct.storage_unit?.name ||
                              '',
                          };
                        }
                      );

                      productsToDisplay = mappedDeliveryProducts.filter(
                        (
                          p
                        ): p is NonNullable<
                          (typeof mappedDeliveryProducts)[0]
                        > => {
                          return p !== null && p.categoryName.trim() !== '';
                        }
                      );
                    }

                    if (productsToDisplay.length > 0) {
                      return (
                        <div className="delivery-products">
                          {productsToDisplay.map((prod, idx) => {
                            const quantityText =
                              prod.quantity &&
                              String(prod.quantity).trim() !== ''
                                ? `${prod.quantity} ${prod.storageUnit}`
                                : prod.storageUnit
                                  ? prod.storageUnit
                                  : '';
                            return (
                              <Label
                                key={prod.id || idx}
                                label={
                                  quantityText
                                    ? `${prod.categoryName}: ${quantityText}`
                                    : prod.categoryName
                                }
                              />
                            );
                          })}
                        </div>
                      );
                    }

                    return (
                      <Label
                        label={intl.formatMessage({
                          id: 'listings.application.all_products_accepted',
                        })}
                      />
                    );
                  })()
                ) : (
                  <>
                    <Label
                      label={intl.formatMessage({
                        id: 'listings.application.other_delivery_options',
                      })}
                      bold
                    />
                    <div className="delivery-products">
                      {(() => {
                        const customProducts = Array.isArray(delivery.products)
                          ? (delivery.products as {
                              listing_product_id: string;
                              product_quantity: number;
                            }[])
                          : [];

                        return customProducts
                          .filter(
                            (prod) =>
                              prod.listing_product_id && prod.product_quantity
                          )
                          .map((prod) => {
                            const listingProduct = listingData?.products?.find(
                              (p) => p.id === prod.listing_product_id
                            );

                            const categoryName = getProductDisplayName(
                              listingProduct,
                              prod.listing_product_id,
                              farmProductNames,
                              applicationProducts
                            );
                            const storageUnitShort =
                              listingProduct?.storage_unit?.short ||
                              listingProduct?.storage_unit?.name ||
                              '';

                            if (!categoryName || categoryName.trim() === '') {
                              return null;
                            }

                            const quantityText = storageUnitShort
                              ? `${prod.product_quantity} ${storageUnitShort}`
                              : prod.product_quantity;

                            return (
                              <Label
                                key={prod.listing_product_id}
                                label={
                                  quantityText
                                    ? `${categoryName}: ${quantityText}`
                                    : categoryName
                                }
                              />
                            );
                          });
                      })()}
                    </div>
                  </>
                )}

                {'delivery_price' in delivery && delivery.delivery_price != null && (
                  <Label
                    label={`${intl.formatMessage({ id: 'listings.application.delivery_price' })}: ${delivery.delivery_price} EUR`}
                    bold
                  />
                )}

                {listingData?.delivery_radius_km && (
                  <Label
                    label={`${intl.formatMessage({ id: 'listings.application.delivery_radius' })}: ${listingData.delivery_radius_km} km`}
                    bold
                  />
                )}

                {warehouseAddress && (
                  <Label
                    label={`${intl.formatMessage({ id: 'listings.application.warehouse_address' })}: ${warehouseAddress}`}
                    bold
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
